import Component from '@glimmer/component';

// ---------------------------------------------------------------------------
// Text – reimagined typography component for body/inline text
// ---------------------------------------------------------------------------

/**
 * Semantic text variant — controls color and default weight.
 *
 * `inherit` is the colorless variant: it injects no color class,
 * so the text takes its color from the surrounding cascade (e.g. a parent
 * recipe class like `.app-cta-banner-description` / `.app-nav-card-subtitle`
 * that drives color from semantic intent tokens). Use it whenever the color
 * is owned by an ancestor recipe rather than by `<Text>` itself — otherwise
 * a legacy raw-gray utility on the variant would win
 * over the recipe's `@layer components` color by cascade order.
 */
export type TextVariant = 'body' | 'muted' | 'label' | 'error' | 'success' | 'warning' | 'inherit';

/**
 * Font size options.
 */
export type TextSize = 'xs' | 'sm' | 'base' | 'lg';

/**
 * Font weight options.
 */
export type TextWeight = 'normal' | 'medium' | 'semibold' | 'bold';

/** Shared body-copy roles that own size, weight, and line-height. */
export type TextRole = 'body' | 'body-secondary' | 'label' | 'caption';

/**
 * HTML element to render.
 */
export type TextTag = 'span' | 'p' | 'label' | 'div';

interface TextSignature {
  Args: {
    /** Semantic variant controlling color. Default: 'body' */
    variant?: TextVariant;
    /** Typography role. Defaults from `@variant`; explicit size and weight override it. */
    role?: TextRole;
    /** Compatibility font-size override applied over the typography role. */
    size?: TextSize;
    /** Compatibility font-weight override applied over the typography role. */
    weight?: TextWeight;
    /** HTML element to render. Default: 'span' */
    tag?: TextTag;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLElement;
}

// ---------------------------------------------------------------------------
// Style maps
// ---------------------------------------------------------------------------

/**
 * Variant → named color class (defined in
 * `addons/tio-ui/src/styles/` as `.tio-text--*`). Each class
 * resolves to a mode-aware semantic token (`--text-primary`,
 * `--success-body`, etc.), so dark mode cascades automatically — no
 * `dark:text-*` utility on each variant.
 *
 * IMPORTANT: this also fixes the child-override trap documented in
 * `.claude/prompts/theme-migration.md` ("Child-override trap — <Text> /
 * <Heading> wrappers"). The old raw light/dark gray utility pair
 * out-cascaded ancestor `.tio-fg-*` recipe colors in dark mode
 * because the Tailwind utilities layer wins over `@layer components`.
 * Named `.tio-text--*` classes live in the same components layer as
 * other recipes, so the cascade is consistent.
 *
 * `inherit` stays empty: parent recipe owns the color.
 */
const VARIANT_CLASSES: Record<TextVariant, string> = {
  body: 'tio-text--body',
  muted: 'tio-text--muted',
  label: 'tio-text--label',
  error: 'tio-text--error',
  success: 'tio-text--success',
  warning: 'tio-text--warning',
  // Colorless: defer to the ancestor cascade (parent recipe class).
  inherit: '',
};

const SIZE_CLASSES: Record<TextSize, string> = {
  xs: 'tio-type-size-xs',
  sm: 'tio-type-size-sm',
  base: 'tio-type-size-base',
  lg: 'tio-type-size-lg',
};

const WEIGHT_CLASSES: Record<TextWeight, string> = {
  normal: 'tio-type-weight-normal',
  medium: 'tio-type-weight-medium',
  semibold: 'tio-type-weight-semibold',
  bold: 'tio-type-weight-bold',
};

const ROLE_CLASSES: Record<TextRole, string> = {
  body: 'tio-type-body',
  'body-secondary': 'tio-type-body-secondary',
  label: 'tio-type-label',
  caption: 'tio-type-caption',
};

const VARIANT_DEFAULT_ROLE: Record<TextVariant, TextRole> = {
  body: 'body',
  muted: 'body-secondary',
  label: 'label',
  error: 'body',
  success: 'body',
  warning: 'body',
  inherit: 'body',
};

/**
 * Text – a typography primitive for body/inline text.
 *
 * Renders a `<span>` (or other tag) with variant-driven color,
 * configurable size and weight.
 *
 * @example Basic
 * ```gts
 * import Text from 'tio-ui/components/text';
 *
 * <Text @variant="muted" @size="sm">Helper text</Text>
 * ```
 *
 * @example As paragraph with weight
 * ```gts
 * <Text @tag="p" @weight="semibold">Important note</Text>
 * ```
 */
export default class Text extends Component<TextSignature> {
  get variant() {
    return this.args.variant ?? 'body';
  }

  get tag() {
    return this.args.tag ?? 'span';
  }

  get variantClass() {
    return VARIANT_CLASSES[this.variant];
  }

  get roleClass() {
    return ROLE_CLASSES[this.args.role ?? VARIANT_DEFAULT_ROLE[this.variant]];
  }

  get sizeClass() {
    return this.args.size ? SIZE_CLASSES[this.args.size] : '';
  }

  get weightClass() {
    return this.args.weight ? WEIGHT_CLASSES[this.args.weight] : '';
  }

  get classes() {
    return [this.variantClass, this.roleClass, this.sizeClass, this.weightClass]
      .filter(Boolean)
      .join(' ');
  }

  <template>
    {{#let (element this.tag) as |Tag|}}
      <Tag data-test-text class={{this.classes}} ...attributes>{{yield}}</Tag>
    {{/let}}
  </template>
}

export type { TextSignature };
